import { TOKEN_KEY } from '@/config/constants' 
import { ElMessage } from 'element-plus'
import request from './request'
import { toPublicMediaUrl } from './publicMediaUrl'

/**
 * 简单的MD5实现（用于文件去重）
 * 注意：这是一个简化的实现，生产环境建议使用 spark-md5 库
 */
class SimpleMD5 {
  constructor() {
    this.blocks = []
    this.buffer8 = new Uint8Array(64)
    this.buffer32 = new Uint32Array(16)
    this.length = 0
    this.buffer = 0
    this.total = 0
  }

  _update() {
    const blocks = this.blocks
    let h0 = 0x67452301
    let h1 = 0xEFCDAB89
    let h2 = 0x98BADCFE
    let h3 = 0x10325476
    let h4 = 0xC3D2E1F0

    for (let i = 0; i < blocks.length; i += 16) {
      const w = blocks.slice(i, i + 16)

      let a = h0
      let b = h1
      let c = h2
      let d = h3
      let e = h4

      for (let j = 0; j < 80; j++) {
        let f = 0
        let k = 0

        if (j < 20) {
          f = (b & c) | (~b & d)
          k = 0x5A827999
        } else if (j < 40) {
          f = b ^ c ^ d
          k = 0x6ED9EBA1
        } else if (j < 60) {
          f = (b & c) | (b & d) | (c & d)
          k = 0x8F1BBCDC
        } else {
          f = b ^ c ^ d
          k = 0xCA62C1D6
        }

        const temp = ((a << 5) | (a >>> 27)) + f + e + k + (w[j] | 0)
        e = d
        d = c
        c = (b << 30) | (b >>> 2)
        b = a
        a = temp
      }

      h0 = (h0 + a) | 0
      h1 = (h1 + b) | 0
      h2 = (h2 + c) | 0
      h3 = (h3 + d) | 0
      h4 = (h4 + e) | 0
    }

    return [h0, h1, h2, h3, h4]
  }

  _hex(buffer) {
    let hex = ''
    for (let i = 0; i < buffer.length; i++) {
      hex += (buffer[i] >>> 4).toString(16)
      hex += (buffer[i] & 0x0F).toString(16)
    }
    return hex
  }

  append(data) {
    if (typeof data === 'string') {
      data = new TextEncoder().encode(data)
    }

    const buffer8 = this.buffer8
    let bufferIdx = this.buffer

    for (let i = 0; i < data.length; i++) {
      buffer8[bufferIdx++] = data[i]
      this.length++

      if (bufferIdx >= 64) {
        this._processBlock(buffer8)
        bufferIdx = 0
      }
    }

    this.buffer = bufferIdx
    this.total += data.length
  }

  _processBlock(data) {
    const blocks = this.blocks
    const buffer32 = this.buffer32

    for (let i = 0; i < 16; i++) {
      buffer32[i] =
        (data[i * 4] << 24) |
        (data[i * 4 + 1] << 16) |
        (data[i * 4 + 2] << 8) |
        data[i * 4 + 3]
    }

    blocks.push(...buffer32)
  }

  end() {
    const buffer8 = this.buffer8
    let bufferIdx = this.buffer

    buffer8[bufferIdx++] = 0x80

    if (bufferIdx > 56) {
      for (let i = bufferIdx; i < 64; i++) {
        buffer8[i] = 0
      }
      this._processBlock(buffer8)
      bufferIdx = 0
    }

    for (let i = bufferIdx; i < 56; i++) {
      buffer8[i] = 0
    }

    const buffer32 = this.buffer32
    for (let i = 0; i < 8; i++) {
      buffer32[14 + i] = (this.total * 8 >>> (i * 8)) & 0xFF
    }

    this._processBlock(buffer8)

    const hash = this._update()
    const result = new Uint8Array(20)
    for (let i = 0; i < 5; i++) {
      for (let j = 0; j < 4; j++) {
        result[i * 4 + j] = (hash[i] >>> (j * 8)) & 0xFF
      }
    }

    return this._hex(result)
  }
}

/**
 * 计算文件的MD5值
 * @param {File} file - 文件对象
 * @returns {Promise<string>} MD5值
 */
export async function calculateFileMD5(file) {
  return new Promise((resolve, reject) => {
    const md5 = new SimpleMD5()
    const reader = new FileReader()
    const chunkSize = 2 * 1024 * 1024 // 2MB per chunk
    let currentChunk = 0
    const chunks = Math.ceil(file.size / chunkSize)

    const loadNext = () => {
      const start = currentChunk * chunkSize
      const end = Math.min(start + chunkSize, file.size)

      reader.onload = (e) => {
        md5.append(new Uint8Array(e.target.result))
        currentChunk++

        if (currentChunk < chunks) {
          loadNext()
        } else {
          resolve(md5.end().toLowerCase())
        }
      }

      reader.onerror = () => {
        reject(new Error('文件读取失败'))
      }

      reader.readAsArrayBuffer(file.slice(start, end))
    }

    loadNext()
  })
}

/**
 * 压缩图片（使用Canvas）
 * @param {File} file - 原始文件
 * @param {Object} options - 压缩选项
 * @returns {Promise<File>} 压缩后的文件
 */
export async function compressImage(file, options = {}) {
  const {
    maxWidth = 4096,
    maxHeight = 4096,
    quality = 0.8,
    maxSize = 8 * 1024 * 1024 // 8MB
  } = options

  // 小于指定大小不压缩
  if (file.size < maxSize) {
    return file
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      const img = new Image()

      img.onload = () => {
        // 计算压缩后的尺寸
        let width = img.width
        let height = img.height

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height)
          width = Math.floor(width * ratio)
          height = Math.floor(height * ratio)
        }

        // 使用Canvas压缩
        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height

        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)

        // 转换为Blob
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error('图片压缩失败'))
              return
            }

            // 转换为File对象
            const compressedFile = new File(
              [blob],
              file.name.replace(/\.[^/.]+$/, '.jpg'),
              { type: 'image/jpeg', lastModified: Date.now() }
            )

            console.log(`图片压缩: ${formatSize(file.size)} → ${formatSize(compressedFile.size)}`)
            resolve(compressedFile)
          },
          'image/jpeg',
          quality
        )
      }

      img.onerror = () => {
        reject(new Error('图片加载失败'))
      }

      img.src = e.target.result
    }

    reader.onerror = () => {
      reject(new Error('文件读取失败'))
    }

    reader.readAsDataURL(file)
  })
}

/**
 * 格式化文件大小
 * @param {number} bytes - 字节数
 * @returns {string} 格式化后的大小
 */
function formatSize(bytes) {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

/**
 * 获取上传签名
 * @param {string} fileName - 文件名
 * @param {number} fileSize - 文件大小
 * @param {string} fileHash - 文件MD5
 * @returns {Promise<Object>} 签名信息
 */
async function getUploadSignature(fileName, fileSize, fileHash, options = {}) {
  const result = await request({
    url: '/api/file/upload/signature',
    silent: options.silent,
    signal: options.signal, expectedAuthToken: options.expectedAuthToken,
    method: 'GET',
    params: {
      fileName,
      fileSize: fileSize.toString(),
      fileHash
    }
  })

  if (result.code !== 200) {
    throw new Error(result.message || '获取签名失败')
  }

  return result.data
}

/**
 * 根据文件名推断Content-Type
 * @param {string} fileName - 文件名
 * @returns {string} Content-Type
 */
function inferContentType(fileName) {
  if (!fileName) return 'application/octet-stream'

  const lowerFileName = fileName.toLowerCase()
  if (lowerFileName.endsWith('.jpg') || lowerFileName.endsWith('.jpeg')) {
    return 'image/jpeg'
  } else if (lowerFileName.endsWith('.png')) {
    return 'image/png'
  } else if (lowerFileName.endsWith('.gif')) {
    return 'image/gif'
  } else if (lowerFileName.endsWith('.bmp')) {
    return 'image/bmp'
  } else if (lowerFileName.endsWith('.webp')) {
    return 'image/webp'
  } else if (lowerFileName.endsWith('.mp4')) {
    return 'video/mp4'
  } else if (lowerFileName.endsWith('.mov')) {
    return 'video/quicktime'
  } else if (lowerFileName.endsWith('.avi')) {
    return 'video/x-msvideo'
  } else if (lowerFileName.endsWith('.mkv')) {
    return 'video/x-matroska'
  } else if (lowerFileName.endsWith('.webm')) {
    return 'video/webm'
  } else if (lowerFileName.endsWith('.mp3')) {
    return 'audio/mpeg'
  } else if (lowerFileName.endsWith('.wav')) {
    return 'audio/wav'
  } else if (lowerFileName.endsWith('.ogg')) {
    return 'audio/ogg'
  } else if (lowerFileName.endsWith('.aac')) {
    return 'audio/aac'
  } else if (lowerFileName.endsWith('.flac')) {
    return 'audio/flac'
  } else if (lowerFileName.endsWith('.m4a')) {
    return 'audio/mp4'
  }
  return 'application/octet-stream'
}

/**
 * 上传文件到OSS
 * @param {string} signedUrl - 签名URL
 * @param {File} file - 文件对象
 * @param {Function} onProgress - 进度回调
 * @returns {Promise<void>}
 */
function uploadToOSS(signedUrl, file, onProgress, signal) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    const abort = () => xhr.abort()
    if (signal?.aborted) return reject(new DOMException('任务已暂停', 'AbortError'))
    signal?.addEventListener('abort', abort, { once: true })
    xhr.onloadend = () => signal?.removeEventListener('abort', abort)
    xhr.onabort = () => reject(new DOMException('任务已暂停', 'AbortError'))

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        const percent = Math.round((e.loaded / e.total) * 100)
        onProgress(percent)
      }
    }

    xhr.onload = () => {
      if (xhr.status === 200) {
        resolve()
      } else {
        // 打印错误详情用于调试
        console.error('OSS上传失败，状态码:', xhr.status)
        console.error('响应内容:', xhr.responseText)
        reject(new Error(`上传失败: ${xhr.status}`))
      }
    }

    xhr.onerror = () => {
      console.error('OSS上传网络错误')
      reject(new Error('网络错误'))
    }

    xhr.ontimeout = () => {
      reject(new Error('上传超时'))
    }

    xhr.timeout = 300000 // 5分钟超时

    xhr.open('PUT', signedUrl)

    // 重要：使用与后端一致的Content-Type推断逻辑
    // 根据文件扩展名推断Content-Type，而不是使用file.type
    const contentType = inferContentType(file.name)
    xhr.setRequestHeader('Content-Type', contentType)

    console.log('上传到OSS:', {
      fileName: file.name,
      contentType: contentType,
      fileSize: file.size
    })

    xhr.send(file)
  })
}

/**
 * 上传完成回调
 * @param {Object} callbackData - 回调数据
 * @returns {Promise<string>} 图片URL
 */
async function uploadCallback(callbackData, options = {}) {
  const result = await request({
    url: '/api/file/upload/callback',
    silent: options.silent,
    signal: options.signal, expectedAuthToken: options.expectedAuthToken,
    method: 'POST',
    data: callbackData
  })

  if (result.code !== 200) {
    throw new Error(result.message || '回调失败')
  }

  return result.data
}

/**
 * 客户端直传图片（主入口函数）
 * @param {File} file - 图片文件
 * @param {Object} options - 选项
 * @param {Function} options.onProgress - 进度回调
 * @param {boolean} options.compress - 是否压缩（默认true）
 * @param {number} options.maxSize - 压缩阈值，默认8MB
 * @returns {Promise<string>} 图片URL
 */
export async function uploadImageDirect(file, options = {}) {
  const {
    onProgress,
    compress = true,
    maxSize = 8 * 1024 * 1024
  } = options

  try {
    // 1. 压缩图片（如果需要）
    let fileToUpload = file
    if (compress) {
      onProgress?.(10)
      fileToUpload = await compressImage(file, { maxSize })
      onProgress?.(20)
    }

    // 2. 计算MD5
    const fileHash = await calculateFileMD5(fileToUpload)
    console.log('文件MD5:', fileHash)
    onProgress?.(30)

    // 3. 获取上传签名
    if (options.signal?.aborted) throw new DOMException('任务已暂停', 'AbortError')
    const signatureData = await getUploadSignature(
      fileToUpload.name,
      fileToUpload.size,
      fileHash, options
    )
    onProgress?.(40)

    // 4. 检查文件是否已存在
    if (signatureData.exists) {
      console.log('✅ 文件已存在，秒传')
      onProgress?.(100)
      return toPublicMediaUrl(signatureData.fileUrl)
    }

    // 5. 直传到OSS
    console.log('开始上传到OSS...')
    await uploadToOSS(signatureData.signedUrl, fileToUpload, (percent) => {
      onProgress?.(40 + Math.floor(percent * 0.5))
    }, options.signal)
    onProgress?.(90)

    // 6. 回调后端保存记录
    const callbackData = {
      fileHash: signatureData.fileHash,
      objectName: signatureData.objectName,
      fileName: file.name,
      fileSize: fileToUpload.size,
      contentType: fileToUpload.type
    }

    if (options.signal?.aborted) throw new DOMException('任务已暂停', 'AbortError')
    const fileUrl = await uploadCallback(callbackData, options)
    console.log('✅ 上传完成:', fileUrl)
    onProgress?.(100)

    return toPublicMediaUrl(fileUrl)

  } catch (error) {
    console.error('上传失败:', error)
    if (!options.silent) ElMessage.error(error.message || '上传失败')
    throw error
  }
}

/**
 * 传统方式上传图片（经过服务器）
 * @param {File} file - 图片文件
 * @param {Function} onProgress - 进度回调
 * @returns {Promise<string>} 图片URL
 */
export async function uploadImageTraditional(file, onProgress) {
  const formData = new FormData()
  formData.append('file', file)

  // 2. 手动获取 Token
  const token = localStorage.getItem(TOKEN_KEY)
  
  // 3. 构造请求头对象
  const headers = {
    // 【关键点1】必须显式将 Content-Type 设为 undefined
    // 这样 Axios 才会移除默认的 application/json，并让浏览器自动生成带 boundary 的 multipart/form-data
    'Content-Type': undefined
  }

  // 【关键点2】手动添加 satoken (Sa-Token)
  if (token) {
    // 移除可能的 Bearer 前缀，直接使用token值
    const tokenValue = token.startsWith('Bearer ') ? token.substring(7) : token
    headers['satoken'] = tokenValue
  }

  try {
    const response = await request({
      url: '/api/file/upload/image',
      method: 'POST',
      data: formData,
      headers: headers, // 4. 将构造好的 headers 传入
      onUploadProgress: (e) => {
        if (e.lengthComputable && onProgress) {
          const percent = Math.round((e.loaded / e.total) * 100)
          onProgress(percent)
        }
      },
      timeout: 300000
    })

    if (response.code === 200) {
      return toPublicMediaUrl(response.data)
    } else {
      throw new Error(response.message || '上传失败')
    }
  } catch (error) {
    throw new Error(error.message || '网络错误')
  }
}


/**
 * 智能上传图片（自动选择最佳方式）
 * 优先使用客户端直传，失败时回退到传统方式
 * @param {File} file - 图片文件
 * @param {Object} options - 选项
 * @returns {Promise<string>} 图片URL
 */
export async function uploadImageSmart(file, options = {}) {
  try {
    // 优先使用客户端直传
    return await uploadImageDirect(file, options)
  } catch (error) {
    console.warn('客户端直传失败，回退到传统方式:', error)
    // 回退到传统方式
    return await uploadImageTraditional(file, options.onProgress)
  }
}

/**
 * 客户端直传视频/音频等通用文件
 * @param {File} file - 文件对象
 * @param {Object} options - 选项
 * @param {Function} options.onProgress - 进度回调
 * @returns {Promise<string>} 文件URL
 */
export async function uploadFileDirect(file, options = {}) {
  const { onProgress } = options

  try {
    onProgress?.(10)

    // 1. 计算MD5
    const fileHash = await calculateFileMD5(file)
    console.log('文件MD5:', fileHash)
    onProgress?.(20)

    // 2. 获取上传签名
    if (options.signal?.aborted) throw new DOMException('任务已暂停', 'AbortError')
    const signatureData = await getUploadSignature(
      file.name,
      file.size,
      fileHash, options
    )
    onProgress?.(30)

    // 3. 检查文件是否已存在（秒传）
    if (signatureData.exists) {
      console.log('文件已存在，秒传')
      onProgress?.(100)
      return toPublicMediaUrl(signatureData.fileUrl)
    }

    // 4. 直传到OSS
    console.log('开始直传到OSS...')
    await uploadToOSS(signatureData.signedUrl, file, (percent) => {
      onProgress?.(30 + Math.floor(percent * 0.6))
    }, options.signal)
    onProgress?.(90)

    // 5. 回调后端保存记录
    const callbackData = {
      fileHash: signatureData.fileHash,
      objectName: signatureData.objectName,
      fileName: file.name,
      fileSize: file.size,
      contentType: file.type
    }

    if (options.signal?.aborted) throw new DOMException('任务已暂停', 'AbortError')
    const fileUrl = await uploadCallback(callbackData, options)
    console.log('上传完成:', fileUrl)
    onProgress?.(100)

    return toPublicMediaUrl(fileUrl)
  } catch (error) {
    console.error('直传失败:', error)
    throw error
  }
}

/**
 * 传统方式上传文件（经过服务器中转）
 * @param {File} file - 文件对象
 * @param {string} category - 文件分类：video、audio
 * @param {Function} onProgress - 进度回调
 * @returns {Promise<string>} 文件URL
 */
export async function uploadFileTraditional(file, category, onProgress) {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('category', category)

  const token = localStorage.getItem(TOKEN_KEY)
  const headers = { 'Content-Type': undefined }
  if (token) {
    const tokenValue = token.startsWith('Bearer ') ? token.substring(7) : token
    headers['satoken'] = tokenValue
  }

  try {
    const response = await request({
      url: '/api/file/upload/file',
      method: 'POST',
      data: formData,
      headers: headers,
      onUploadProgress: (e) => {
        if (e.lengthComputable && onProgress) {
          const percent = Math.round((e.loaded / e.total) * 100)
          onProgress(percent)
        }
      },
      timeout: 300000
    })

    if (response.code === 200) {
      return toPublicMediaUrl(response.data)
    } else {
      throw new Error(response.message || '上传失败')
    }
  } catch (error) {
    throw new Error(error.message || '网络错误')
  }
}

/**
 * 智能上传视频/音频文件（自动选择最佳方式）
 * 优先使用客户端直传，失败时回退到传统方式
 * @param {File} file - 文件对象
 * @param {string} category - 文件分类：video、audio
 * @param {Object} options - 选项
 * @returns {Promise<string>} 文件URL
 */
export async function uploadFileSmart(file, category, options = {}) {
  try {
    return await uploadFileDirect(file, options)
  } catch (error) {
    console.warn('客户端直传失败，回退到传统方式:', error)
    return await uploadFileTraditional(file, category, options.onProgress)
  }
}
